import { toInteger } from 'lodash';
import moment from 'moment';
import React from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import {
  TextArea,
  Form,
  Select,
  Loader,
  Dimmer,
  Button,
  Modal,
  Checkbox,
  Message,
} from 'semantic-ui-react';

import { PRIORITY_CATEGORIES, PRIORITY_MAP, TASK_CATEGORIES } from './constants';
import { useAddTaskForm } from './hooks';
import type { AddTaskProps } from './types';

const AddTask: React.FC<AddTaskProps> = (props) => {
  const { handleAddTaskToggle, isAddTaskOpen } = props;
  const {
    clearForm,
    dimmerProcess,
    flatPractitioners,
    formCode,
    formDate,
    formDescription,
    formEndDate,
    formOwner,
    formPriorirty,
    formStartDate,
    formVendor,
    handleChange,
    handleSubmit,
    recurring,
    recurringInterval,
    referralId,
    setFormEndDate,
    setFormStartDate,
    setRecurring,
    setRecurringInterval,
    validationErrors,
    vendorList,
  } = useAddTaskForm(props);

  const temp = TASK_CATEGORIES.map((category) => ({
    key: category,
    text: category.toUpperCase(),
    value: category,
  }));

  const priorityOptions = PRIORITY_CATEGORIES.map((priority) => ({
    key: priority,
    text: (PRIORITY_MAP as Record<string, string>)[priority]!.toUpperCase(),
    value: priority,
  }));

  const practitionerList = flatPractitioners.map((flatPractitioner) => ({
    key: flatPractitioner.id,
    text: flatPractitioner.fullName,
    value: flatPractitioner.id.toString(),
  }));

  const hasFormDate =
    formDate !== null && moment(formDate).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD');

  return (
    <Modal
      as={Form}
      onOpen={() => handleAddTaskToggle(true)}
      onClose={() => handleAddTaskToggle(false)}
      open={isAddTaskOpen}
      onSubmit={handleSubmit}
      trigger={<Button icon="add" content="Create Task" color="green" size="small" />}
      size="large"
      verticalAlign="middle"
    >
      <Modal.Content>
        {validationErrors.length > 0 && (
          <Message negative>
            <Message.Header>Error:</Message.Header>
            <Message.List>
              {validationErrors.map((error, index) => (
                <Message.Item key={index}>{error}</Message.Item>
              ))}
            </Message.List>
          </Message>
        )}
        <Form.Group widths={4}>
          <Form.Input
            autoComplete="off"
            value={formCode}
            onChange={handleChange}
            fluid
            name="code"
            label="Task Category:"
            control={Select}
            options={temp}
            required
            selectOnBlur={false}
          />
          <Form.Input
            autoComplete="off"
            value={formPriorirty}
            onChange={handleChange}
            fluid
            name="priority"
            label="Task Priority:"
            control={Select}
            options={priorityOptions}
            required
            selectOnBlur={false}
          />
          {(props?.referralIdList?.length ?? 0) > 0 && (
            <Form.Input
              name="referralId"
              label="REFERRAL ID:"
              value={referralId}
              type="text"
              required
              selectOnBlur={false}
              control={Select}
              options={props?.referralIdList?.map((item) => ({
                key: item?.referralId,
                value: item?.referralId,
                /*
                 * SUSPECTED DEFECT — REPORTED, NOT FIXED. `ReferralSummary.referralId`
                 * is optional, and this reads it unguarded, so a referral saved without
                 * an id throws here and the Add Task form fails to render its referral
                 * picker. `../../filters` renders the same list as
                 * `k?.referralId?.toUpperCase()`, which yields `undefined` and offers a
                 * blank option instead. The two disagree about what an absent id means;
                 * one of them is wrong, and deciding which changes behaviour either way.
                 */
                text: item?.referralId!.toUpperCase(),
              }))}
              onChange={handleChange}
            />
          )}
        </Form.Group>
        <Form.Input
          control={Checkbox}
          checked={recurring}
          label="This is a recurring task"
          onChange={(e, checkprops) => {
            setRecurring(checkprops['checked'] as boolean);
            setFormEndDate(null);
          }}
        />
        {!recurring ? (
          <Form.Group widths="equal">
            <Form.Input
              autoComplete="off"
              value={formDate}
              onChange={handleChange}
              name="endDate"
              label="Task Date:"
              control={SemanticDatepicker}
              format="YYYY-MM-DD"
              required
              selectOnBlur={false}
              default={moment().format('YYYY-MM-DD')}
            />
          </Form.Group>
        ) : (
          <Form.Group widths="equal">
            <>
              <Form.Input
                autoComplete="off"
                value={formStartDate}
                onChange={(e, fieldprops) => {
                  setFormStartDate(fieldprops.value);
                }}
                name="recurringStartDate"
                label="Recurring Start Date:"
                control={SemanticDatepicker}
                format="YYYY-MM-DD"
                required
                selectOnBlur={false}
              />
              <Form.Input
                autoComplete="off"
                value={formEndDate}
                onChange={(e, fieldprops) => {
                  setFormEndDate(fieldprops.value);
                }}
                name="recurringEndDate"
                label="Recurring End Date:"
                control={SemanticDatepicker}
                format="YYYY-MM-DD"
                required
                selectOnBlur={false}
              />
              <Form.Input
                autoComplete="off"
                label="Interval (days)"
                type="number"
                name="interval"
                min={1}
                value={recurringInterval ?? ''}
                onChange={(e, fieldprops) => {
                  setRecurringInterval(toInteger(fieldprops.value));
                }}
                required
              />
            </>
          </Form.Group>
        )}
        {hasFormDate ? (
          <Message className="red inline fields" size="tiny">
            Please select a valid date. The due date cannnot be updated to a pastdate.
          </Message>
        ) : null}
        <Form.Group widths="equal">
          <Form.Input
            autoComplete="off"
            value={formOwner}
            onChange={handleChange}
            name="ownerRef"
            control={Select}
            search
            options={practitionerList}
            label="Assigned to:"
            selectOnBlur={false}
            required
          />
          {formCode === 'food-delivery' && (
            <Form.Input
              value={formVendor}
              onChange={handleChange}
              name="vendor"
              control={Select}
              search
              options={vendorList}
              label="Vendor"
              selectOnBlur={false}
            />
          )}
        </Form.Group>
        <Form.Field
          autoComplete="off"
          value={formDescription}
          onChange={handleChange}
          name="description"
          control={TextArea}
          label="Task Description:"
          placeholder="Enter Task Description"
          required
        />
        {dimmerProcess && (
          <Dimmer active>
            <Loader active inline />
          </Dimmer>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button content="Clear" onClick={clearForm} />
        <Button
          content="Close"
          type="button"
          color="red"
          onClick={() => handleAddTaskToggle(false)}
        />
        <Button
          content="Create Task"
          type="submit"
          positive
          className={hasFormDate ? 'disabled field' : ''}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default AddTask;
